import * as React from 'react';
import { SxProps, Tabs, Tab, TabsProps, Theme, Box, tabClasses, tabsClasses } from '@mui/material';
import { IconKey, Icon } from '../Icon';
import { IconButton, IIconButtonProps } from '../IconButton';
import { theme } from '../../utils';

const styles: Record<string, SxProps<Theme>> = {
  tabBarContainer: {
    width: '100%',
    [`& .${tabClasses['root']}`]: {
      minWidth: '120px',
      paddingX: 1.5,
      paddingY: 0.75
    },
    [`& .${tabsClasses['scrollButtons']}`]: {
      display: 'inherit'
    },
    // ie11 only - hide scrollbars
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      '& [class*="MuiTabs-scrollable"]': {
        overflowX: 'hidden'
      }
    }
  },
  verticalTabBarContainer: {
    flexGrow: 1,
    display: 'flex',
    marginRight: 2,
    [`& .${tabClasses['root']}`]: {
      minWidth: '120px',
      paddingX: 1.5,
      paddingBottom: 0.75
    }
  },
  activeTab: {
    [`& .${tabsClasses['indicator']}`]: { top: 0 }
  },
  activeVerticalTab: {
    [`&.${tabClasses['root']}.Mui-selected`]: { borderLeft: 'none', borderRight: 'none' }
  },
  inactiveTab: {
    '&:hover': {
      background: theme.palette.grey[300]
    }
  },
  inactiveVerticalTab: {
    textAlign: 'right',
    '&:hover': {
      background: theme.palette.grey[300]
    }
  },
  secondaryActiveTab: {
    textTransform: 'none',
    [`&.${tabClasses['root']}.Mui-selected`]: {
      borderLeft: 'none',
      borderRight: 'none'
    }
  },
  secondaryInactiveTab: {
    textTransform: 'none',
    borderBottom: '1px solid',
    borderColor: 'grey.600',
    '&:hover': {
      background: theme.palette.grey[300]
    },
    '&:focusVisible': {
      background: theme.palette.grey[300]
    }
  },
  horizontalBorder: {
    borderBottom: '1px solid',
    borderColor: 'action.disabled'
  },
  verticalTabs: {
    borderRight: '1px solid',
    borderColor: 'grey.600',
    alignItems: 'flex-end'
  },
  tabPanel: {
    marginTop: 1
  },
  verticalTabPanel: {
    marginTop: 1,
    marginLeft: 1,
    maxWidth: '70%'
  },
  tabBarExtender: {
    width: '100%',
    borderBottom: '1px solid',
    borderColor: 'grey.600'
  },
  iconTab: {
    minHeight: 0,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    '& > *:first-child': {
      marginBottom: 0
    },
    display: 'flex',
    alignItems: 'center',
    [`&.${tabClasses['root']}`]: {
      paddingTop: 1.125,
      paddingBottom: 0.75
    }
  },
  verticalIconTab: {
    '& > *:first-child': {
      marginBottom: 0.5
    }
  },
  icon: {
    marginRight: 1
  },
  dragOverBorder: {
    borderRight: '3px solid',
    borderColor: 'primary.main'
  }
};

interface Tab {
  label: string | React.ReactNode;
  disabled?: boolean;
  key?: number;
  component?: any;
  contentItem?: any;
  className?: string;
  icon?: IconKey;
  iconColor?: string;
  tabId?: number;
  draggable?: boolean;
}

interface Tabs {
  index?: number;
  value?: number;
  key?: number;
  component?: any;
}

interface TabPanel {
  index?: number;
  value?: number;
  children?: React.ReactNode;
  contentItem?: any;
  key?: number;
  useStyles?: any;
  className?: any;
  sx?: SxProps<Theme>;
  style?: any;
  showPrimaryContent?: boolean;
}

type Variant = 'standard' | 'scrollable';

export interface TabBarProps {
  primaryTabs: Tab[];
  primaryTabPanels: TabPanel[];
  variant?: Variant;
  index?: number;
  inlineAction?: () => void;
  inlineActionIcon?: IIconButtonProps['icon'];
  inlineActionLabel?: string;
  orientation?: TabsProps['orientation'];
  elevation?: number;
  component?: any;
  onChange?: (event: React.ChangeEvent<{}>, newValue: number) => void;
  defaultValue?: number;
  className?: string;
  externalState?: boolean;
  externalValue?: number;
  selectionFollowsFocus?: boolean;
  secondaryStyling?: boolean;
  sx?: SxProps<Theme>;
  onDragStart?: (event: React.DragEvent, id: string | number) => void;
  onDrag?: (event: React.DragEvent, id: string | number) => void;
  onDragEnd?: (event: React.DragEvent, id: string | number) => void;
  onDragEnter?: (event: React.DragEvent, id: string | number) => void;
  onDragExit?: (event: React.DragEvent, id: string | number) => void;
  onDragLeave?: (event: React.DragEvent, id: string | number) => void;
  onDragOver?: (event: React.DragEvent, id: string | number) => void;
  onDrop?: (event: React.DragEvent, id: string | number) => void;
}

const TabPanel = (props: TabPanel) => {
  const { children, value, index, className, sx, ...other } = props;
  return (
    <Box role="tabpanel" sx={sx} className={className} {...other}>
      {value === index ? children : null}
    </Box>
  );
};

const TabBarDivider = (props: any) => {
  const { tabBarDividerStyle } = props;
  return <Box sx={tabBarDividerStyle} className={'tabBarDivider'}></Box>;
};

const TabBar = ({
  primaryTabs,
  primaryTabPanels,
  orientation,
  component,
  onChange,
  defaultValue,
  externalState,
  externalValue,
  selectionFollowsFocus,
  secondaryStyling,
  sx,
  variant = 'scrollable',
  onDragStart,
  onDragEnd,
  onDrop,
  inlineAction,
  inlineActionIcon,
  inlineActionLabel
}: TabBarProps) => {
  const {
    tabBarContainer,
    verticalTabBarContainer,
    activeTab,
    activeVerticalTab,
    inactiveTab,
    inactiveVerticalTab,
    secondaryInactiveTab,
    secondaryActiveTab,
    horizontalBorder,
    tabPanel,
    verticalTabPanel,
    verticalTabs,
    tabBarExtender,
    iconTab,
    verticalIconTab,
    icon,
    dragOverBorder
  } = styles;

  const [value, setValue] = React.useState(defaultValue || 0);
  const [dragOverIndex, setDragOverindex] = React.useState<number | null>(null);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
    onChange && onChange(event, newValue);
  };

  /* istanbul ignore next */
  const handleDragStart = (event: React.DragEvent, id: number) => {
    // Fires once at the start of a drag event
    onDragStart && onDragStart(event, id);
  };
  /* istanbul ignore next */
  const handleDragOver = (event: React.DragEvent, tabIndex: number) => {
    // fires every few milliseconds while a draggable element is over a valid drop target
    // Set to prevent default if this a valid drop target
    event.preventDefault();
    if (tabIndex === dragOverIndex) {
      return;
    } else {
      setDragOverindex(tabIndex);
    }
  };
  /* istanbul ignore next */
  const handleDragEnd = (event: React.DragEvent, id: number) => {
    event.preventDefault();
    onDragEnd && onDragEnd(event, id);
    setDragOverindex(null);
  };
  /* istanbul ignore next */
  const handleDrop = (event: React.DragEvent, tabIndex: number) => {
    event.preventDefault();
    event.stopPropagation();
    onDrop && onDrop(event, tabIndex);
  };

  const activeTabStyles = secondaryStyling ? secondaryActiveTab : activeTab;
  const inactiveTabStyles = secondaryStyling ? secondaryInactiveTab : ({ ...horizontalBorder, ...inactiveTab } as SxProps<Theme>);

  const activeOrInactive = (activeValue: any, tabIndex: any): SxProps<Theme> => {
    return activeValue === tabIndex ? activeTabStyles : inactiveTabStyles;
  };

  const verticalActiveOrInactive = (activeValue: any, tabIndex: any): SxProps<Theme> => {
    return activeValue === tabIndex ? activeVerticalTab : inactiveVerticalTab;
  };

  const InlineActionButton = ({
    inlineAction,
    inlineActionIcon,
    inlineActionLabel
  }: Pick<TabBarProps, 'inlineAction' | 'inlineActionIcon' | 'inlineActionLabel'>) => {
    return <IconButton icon={inlineActionIcon ? inlineActionIcon : 'Info'} onClick={inlineAction} label={inlineActionLabel} />;
  };

  return (
    <Box sx={orientation === 'vertical' ? verticalTabBarContainer : tabBarContainer}>
      <Tabs
        value={externalState ? externalValue : value}
        onChange={externalState ? onChange : handleChange}
        textColor="primary"
        orientation={orientation}
        sx={[orientation === 'vertical' ? verticalTabs : activeTabStyles, sx] as SxProps<Theme>}
        component={component}
        data-test={orientation === 'vertical' ? `VerticalTabBar` : `TabBar`}
        selectionFollowsFocus={selectionFollowsFocus}
        variant={variant}
      >
        {primaryTabs.map((primaryTab: Tab, tabIndex: number) => {
          const tabName = orientation === 'vertical' ? `tab-vertical-${tabIndex}` : `tab-${tabIndex}`;
          const activeValue = externalState ? externalValue : value;
          return (
            <Tab
              draggable={primaryTab.draggable}
              onDragStart={(event: React.DragEvent) => handleDragStart(event, primaryTab.tabId || tabIndex)}
              onDragEnd={(event: React.DragEvent) => handleDragEnd(event, primaryTab.tabId || tabIndex)}
              onDragOver={(event: React.DragEvent) => handleDragOver(event, tabIndex)}
              onDrop={(event: React.DragEvent) => handleDrop(event, tabIndex)}
              id={primaryTab.tabId}
              key={tabName}
              label={primaryTab.label}
              sx={
                [
                  orientation === 'vertical' ? verticalActiveOrInactive(activeValue, tabIndex) : activeOrInactive(activeValue, tabIndex),
                  primaryTab.icon ? iconTab : undefined,
                  primaryTab.icon && orientation === 'vertical' ? verticalIconTab : undefined,
                  dragOverIndex === tabIndex ? dragOverBorder : undefined
                ] as SxProps<Theme>
              }
              disabled={primaryTab.disabled}
              component={component}
              data-test={tabName}
              icon={primaryTab.icon && <Icon icon={primaryTab.icon} fontSize="small" sx={icon} style={{ color: primaryTab.iconColor }} />}
              disableFocusRipple
            />
          );
        })}
        {orientation !== 'vertical' ? <TabBarDivider tabBarDividerStyle={tabBarExtender} /> : null}
        {inlineAction && orientation != 'vertical' && (
          <InlineActionButton inlineAction={inlineAction} inlineActionIcon={inlineActionIcon} inlineActionLabel={inlineActionLabel} />
        )}
      </Tabs>
      {primaryTabPanels.map((tabPanelDisplay: TabPanel, tabPanelIndex: number) => {
        const tabPanelKey = tabPanelIndex;
        const tabPanelName = orientation === 'vertical' ? `tabpanel-vertical-${tabPanelIndex}` : `tabpanel-${tabPanelIndex}`;
        return (
          <TabPanel
            value={externalState ? externalValue : value}
            index={tabPanelDisplay.index}
            key={tabPanelKey}
            data-test={tabPanelName}
            sx={orientation === 'vertical' ? verticalTabPanel : tabPanel}
            className={'tabNestedWrapper'}
          >
            {tabPanelDisplay.contentItem}
          </TabPanel>
        );
      })}
    </Box>
  );
};

TabBar.defaultProps = {
  variant: 'scrollable',
  externalState: false
};

TabBar.displayName = 'TabBar';

export { TabBar };
